import React, { useEffect, useState } from 'react';
import { userService } from '../services/api';
import { UserFormModal } from '../components/UserFormModal';
import { Sidebar } from '../components/Sidebar';
import { UserAvatar } from '../components/UserAvatar';
import { useAuth } from '../context/AuthContext';
import { useSidebar } from '../context/SidebarContext';
import { getGenderInVietnamese } from '../utils/genderHelper';
import { canViewUser, canEditUser, canCreateUser, canDeleteUser } from '../utils/familyTreeHelper';
import '../styles/users.scss';
import '../styles/layout.scss';

export const UsersPage: React.FC = () => {
  const USERS_PER_PAGE = 10;
  const [users, setUsers] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<any>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [deleteConfirm, setDeleteConfirm] = useState<number | null>(null);
  const [resetMessage, setResetMessage] = useState('');
  const [openMenuId, setOpenMenuId] = useState<number | null>(null);
  const { user: currentUser } = useAuth();
  const { toggleSidebar } = useSidebar();

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    try {
      setIsLoading(true);
      setError('');
      const response = await userService.getAllUsers();
      if (!response) {
        setUsers([]);
        setError('Không nhận được dữ liệu người dùng từ máy chủ');
        return;
      }

      if (!response?.success || !Array.isArray(response.data)) {
        setUsers([]);
        setError(response?.message || 'Không thể tải danh sách người dùng');
        return;
      }

      setUsers(response.data);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load users');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteUser = async (id: number) => {
    try {
      await userService.deleteUser(id);
      setUsers(users.filter((u) => u.id !== id));
      setDeleteConfirm(null);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to delete user');
    }
  };

  const handleResetPassword = async (id: number) => {
    const userToReset = users.find((u) => u.id === id);
    const userName = userToReset?.fullName || 'người dùng này';

    if (!window.confirm(`Bạn có chắc muốn đặt lại mật khẩu cho ${userName}?`)) {
      return;
    }

    try {
      setError('');
      setResetMessage('');
      const response = await userService.resetPassword(id);
      if (response.success) {
        setResetMessage(`Mật khẩu mới cho ${userName}: ${response.data?.temporaryPassword}`);
      } else {
        setError(response.message || 'Failed to reset password');
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to reset password');
    }
  };

  const handleAddClick = () => {
    if (!canCreateUser(currentUser)) {
      return;
    }

    setSelectedUser(null);
    setIsFormOpen(true);
  };

  const handleEditClick = (user: any) => {
    if (!canEditUser(currentUser, user, users)) {
      return;
    }

    setSelectedUser(user);
    setIsFormOpen(true);
  };

  const handleFormClose = () => {
    setIsFormOpen(false);
    setSelectedUser(null);
  };

  const handleFormSuccess = () => {
    loadUsers();
  };

  const filteredUsers = users.filter((user) => {
    const matchesSearch =
      user.fullName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (user.homeName || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.email.toLowerCase().includes(searchTerm.toLowerCase());

    return matchesSearch && canViewUser(currentUser, user, users);
  });
  const totalPages = Math.ceil(filteredUsers.length / USERS_PER_PAGE);
  const paginatedUsers = filteredUsers.slice(
    (currentPage - 1) * USERS_PER_PAGE,
    currentPage * USERS_PER_PAGE
  );

  useEffect(() => {
    if (totalPages > 0 && currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  return (
    <div className="main-layout">
      <Sidebar />
      <div className="main-content">
        <div className="header">
          <button className="hamburger-btn" onClick={toggleSidebar} aria-label="Toggle sidebar">
            <i className="fas fa-bars"></i>
          </button>
          <h1>
            <i className="fas fa-users"></i> Quản lý người dùng
          </h1>
        </div>

        <div className="content-area">
          {error && <div className="alert alert-danger">{error}</div>}

          <div className="users-container">
            <div className="users-header">
              <h2>Người dùng</h2>
              {canCreateUser(currentUser) && (
                <button className="btn btn-primary" onClick={handleAddClick}>
                  <i className="fas fa-plus"></i> Thêm người dùng
                </button>
              )}
            </div>

            {resetMessage && (
              <div className="password-reset-message">
                <strong>Đã đặt lại mật khẩu:</strong> {resetMessage}
              </div>
            )}

            <div className="search-bar">
              <input
                type="text"
                placeholder="Tìm kiếm theo tên, tên gọi ở nhà hoặc email..."
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setCurrentPage(1);
                }}
              />
            </div>

            {isLoading ? (
              <div className="loading">
                <div className="spinner"></div>
              </div>
            ) : filteredUsers.length === 0 ? (
              <div className="empty-state">
                <i className="fas fa-inbox"></i>
                <p>
                  {users.length === 0
                    ? 'Không tìm thấy người dùng nào. Nhấp "Thêm người dùng" để tạo.'
                    : 'Không tìm thấy người dùng phù hợp với tìm kiếm của bạn.'}
                </p>
              </div>
            ) : (
              <div className="table-responsive">
                <table>
                  <thead>
                    <tr>
                      <th>ID</th>
                      <th>Họ và tên</th>
                      <th>Email</th>
                      <th>Giới tính</th>
                      <th>Ngày sinh</th>
                      <th>Cha/Mẹ</th>
                      <th>Vai trò</th>
                      <th>Hành động</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedUsers.map((user) => {
                      const canEdit = canEditUser(currentUser, user, users);
                      const canDelete = canDeleteUser(currentUser, user, users);
                      const canReset = currentUser?.isAdmin === true;
                      const hasActions = canEdit || canDelete || canReset;

                      return (
                      <tr key={user.id}>
                        <td>{user.id}</td>
                        <td>
                          <div className="user-cell">
                            <UserAvatar
                              imageUrl={user.imageUrl}
                              name={user.fullName}
                              size="sm"
                            />
                            <span>{user.fullName}</span>
                          </div>
                        </td>
                        <td>{user.email}</td>
                        <td>{getGenderInVietnamese(user.gender)}</td>
                        <td>
                          {[user.birthDay, user.birthMonth, user.birthYear].filter(Boolean).join('/') || '-'}
                        </td>
                        <td>{user.parentName || '-'}</td>
                        <td>
                          {user.isAdmin ? (
                            <span style={{ color: '#f39c12' }}>
                              <i className="fas fa-shield-alt"></i> System Admin
                            </span>
                          ) : user.isFamilyAdmin ? (
                            <span style={{ color: '#27ae60' }}>
                              <i className="fas fa-users-cog"></i> Family Admin
                            </span>
                          ) : (
                            <span style={{ color: '#7f8c8d' }}>
                              <i className="fas fa-user"></i> User
                            </span>
                          )}
                        </td>
                        <td>
                          {hasActions && (
                          <div className="action-menu-wrapper">
                            <button
                              className="action-menu-trigger"
                              onClick={() => setOpenMenuId(openMenuId === user.id ? null : user.id)}
                              title="Tùy chọn"
                            >
                              <i className="fas fa-ellipsis-v"></i>
                            </button>

                            {openMenuId === user.id && (
                              <div className="action-menu">
                                {canEdit && (
                                  <button
                                    className="action-menu-item"
                                    onClick={() => {
                                      setOpenMenuId(null);
                                      handleEditClick(user);
                                    }}
                                  >
                                    <i className="fas fa-edit"></i> Sửa
                                  </button>
                                )}
                                {canReset && (
                                  <button
                                    className="action-menu-item"
                                    onClick={() => {
                                      setOpenMenuId(null);
                                      handleResetPassword(user.id);
                                    }}
                                  >
                                    <i className="fas fa-key"></i> Reset mật khẩu
                                  </button>
                                )}
                                {canDelete && (
                                  <button
                                    className="action-menu-item danger"
                                    onClick={() => {
                                      setOpenMenuId(null);
                                      setDeleteConfirm(user.id);
                                    }}
                                  >
                                    <i className="fas fa-trash"></i> Xóa
                                  </button>
                                )}
                              </div>
                            )}
                          </div>
                          )}

                          {deleteConfirm === user.id && (
                            <div className="delete-confirm">
                              <button
                                className="btn btn-danger btn-sm"
                                onClick={() => handleDeleteUser(user.id)}
                              >
                                Xác nhận xóa
                              </button>
                              <button
                                className="btn btn-sm btn-cancel"
                                onClick={() => setDeleteConfirm(null)}
                              >
                                Hủy
                              </button>
                            </div>
                          )}
                        </td>
                      </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            {!isLoading && filteredUsers.length > 0 && (
              <div className="users-pagination" aria-label="Phân trang danh sách người dùng">
                <span className="users-pagination__summary">
                  Hiển thị {(currentPage - 1) * USERS_PER_PAGE + 1}-
                  {Math.min(currentPage * USERS_PER_PAGE, filteredUsers.length)} trong {filteredUsers.length} người dùng
                </span>
                {totalPages > 1 && (
                  <div className="users-pagination__controls">
                    <button
                      type="button"
                      className="users-pagination__button"
                      onClick={() => setCurrentPage((page) => Math.max(page - 1, 1))}
                      disabled={currentPage === 1}
                      aria-label="Trang trước"
                    >
                      <i className="fas fa-chevron-left"></i>
                    </button>
                    {Array.from({ length: totalPages }, (_, index) => index + 1).map((page) => (
                      <button
                        type="button"
                        key={page}
                        className={`users-pagination__button${page === currentPage ? ' active' : ''}`}
                        onClick={() => setCurrentPage(page)}
                        aria-label={`Trang ${page}`}
                        aria-current={page === currentPage ? 'page' : undefined}
                      >
                        {page}
                      </button>
                    ))}
                    <button
                      type="button"
                      className="users-pagination__button"
                      onClick={() => setCurrentPage((page) => Math.min(page + 1, totalPages))}
                      disabled={currentPage === totalPages}
                      aria-label="Trang sau"
                    >
                      <i className="fas fa-chevron-right"></i>
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      <UserFormModal
        user={selectedUser}
        isOpen={isFormOpen}
        onClose={handleFormClose}
        onSuccess={handleFormSuccess}
      />
    </div>
  );
};
